import type { EquipmentInfo, ExerciseInfoId, MovementInfo } from '@az3fr/core';
import { Field, Label, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ArrowRightIcon, CheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toast } from 'sonner';

import { Drawer } from '@/components/app/drawer';
import { Badge } from '@/components/ui/badge';
import { VirtualSelect } from '@/components/ui/virtual-select';
import { createGroupedEquipmentOptions } from '@/entities/equipment/lib/group-equipment-options';
import { equipmentSliceSelectors } from '@/entities/equipment/model/equipment.selector';
import { exerciseEntityThunks } from '@/entities/exercise/model/exercise.thunks';
import { CreateSelectMovementCombobox } from '@/entities/movement/ui/create-select-movement-combobox';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';
import { useIsMobile } from '@/hooks/use-is-mobile.hook';

function CreateUserExerciseDrawerV2(props: {
	/**
	 * Whether the drawer is open.
	 */
	isOpen: boolean;
	/**
	 * close the drawer callback.
	 */
	close: () => void;
	/**
	 * The initial query for the movement search.
	 */
	movementQuery?: string;
	/**
	 * Parent can provide a callback to be called when an exercise is created.
	 */
	onCreate?: (exerciseId: ExerciseInfoId) => void;
}) {
	const { isOpen, close, movementQuery, onCreate } = props;
	const dispatch = useAppDispatch();
	const equipments = useAppSelector(equipmentSliceSelectors.selectLoadEquipments);
	const isMobile = useIsMobile();
	const [selectedMovement, setSelectedMovement] = useState<MovementInfo | null>(null);
	const [selectedEquipment, setSelectedEquipment] = useState<EquipmentInfo | null>(null);
	const canSubmit = selectedMovement && selectedEquipment;

	function handleMovementChange(movement: MovementInfo | null) {
		setSelectedMovement(movement);
	}
	function handleClose() {
		setSelectedMovement(null);
		setSelectedEquipment(null);
		close();
	}
	function handleCancel() {
		handleClose();
	}

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		if (selectedMovement && selectedEquipment) {
			try {
				const exerciseId = await dispatch(
					exerciseEntityThunks.createNewExercise({
						movementId: selectedMovement.id,
						equipmentId: selectedEquipment.id,
					}),
				).unwrap(); // `unwrap` will THROW if the thunk is rejected

				// --- SUCCESS ---
				// Show success toast with formatted exercise name
				toast.success(
					<div className="flex flex-col gap-1">
						<span className="font-medium text-gray-500 text-xs">Exercise created</span>
						<div className="flex items-center gap-2">
							<span className="font-medium text-gray-900 text-sm">{selectedMovement.name}</span>
							<span className="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-gray-50 px-2 py-0.5 font-medium text-gray-600 text-xs">
								{selectedEquipment.name}
							</span>
						</div>
					</div>,
				);

				// Call the callback with the exercise ID
				onCreate?.(exerciseId);

				// Close drawer whether exercise was created or already existed
				handleClose();
			} catch (error) {
				console.error(error);
				// @todo: Silently fail - could add proper error handling if needed
				// maybe we can handle that in 'extraReducers' because a `rejected` action will be dispatched.
			}
		}
	}

	return (
		<Drawer
			fullScreen={isMobile}
			size="md"
			isOpen={isOpen}
			close={handleClose}
			transitionDuration={300}
			direction="right"
			renderCloseButton={(closeDrawer) => (
				<header className="relative border-gray-200 border-b bg-white/80 px-4 py-3 backdrop-blur-sm sm:px-5">
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-3">
							<ExerciseInfoPopover />
							<div className="space-y-1">
								<h3 className="font-semibold text-base text-gray-900 leading-tight sm:text-lg">
									Create Exercise
								</h3>
								<p className="font-medium text-gray-500 text-xs uppercase tracking-widest">
									Movement + Primary Load
								</p>
							</div>
						</div>
						<button
							type="button"
							onClick={closeDrawer}
							className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 shadow-sm transition-colors duration-200 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
							aria-label="Go back"
						>
							<ArrowRightIcon className="size-4" />
						</button>
					</div>
				</header>
			)}
		>
			<div className="flex flex-col bg-linear-to-b from-white via-white to-gray-50/60">
				<form onSubmit={handleSubmit} className="flex h-full flex-col">
					{/* ---- Form Content ---- */}
					<div className="flex-1 space-y-6 overflow-y-auto px-4 py-6 sm:px-5">
						{/* ---- Movement Field ---- */}
						<Field>
							<Label className="mb-1.5 flex items-center justify-start font-medium text-gray-700 text-xs sm:text-sm">
								{selectedMovement && (
									<CheckIcon className="fade-in-0 zoom-in-95 me-2 inline-flex size-4 animate-in text-emerald-500 duration-200" />
								)}
								Movement
								<span className="ms-1 text-rose-500">*</span>
							</Label>

							{/* ---- Movement Combobox ---- */}
							<CreateSelectMovementCombobox
								onChange={handleMovementChange}
								initialQuery={movementQuery}
							/>

							{/* ---- Movement Hint ---- */}
							<div className="mt-1.5 flex items-start gap-1.5 rounded-md bg-gray-50/50 px-2 py-1.5">
								<div className="mt-0.5 size-1.5 shrink-0 rounded-full bg-gray-300" />
								<p className="text-gray-500 text-xs">
									Choose a generic movement pattern (e.g., "squat", "press", "pull") or create a new
									one. Add equipment below.
								</p>
							</div>
						</Field>

						{/* ---- Equipment Field ---- */}
						<Field>
							<Label className="mb-1.5 flex items-center justify-start font-medium text-gray-700 text-xs sm:text-sm">
								{selectedEquipment && (
									<CheckIcon className="fade-in-0 zoom-in-95 me-2 inline-flex size-4 animate-in text-emerald-500 duration-200" />
								)}
								Primary Load
								<span className="ml-1 text-rose-500">*</span>
							</Label>
							<VirtualSelect
								value={
									selectedEquipment
										? {
												value: selectedEquipment.id,
												label: selectedEquipment.name,
											}
										: null
								}
								onChange={(option) => {
									setSelectedEquipment(
										equipments.find((equipment) => equipment.id === option?.value) ?? null,
									);
								}}
								disabled={equipments.length === 0}
								options={createGroupedEquipmentOptions(equipments)}
								placeholder="Select equipment"
								buttonClassName="h-9 justify-start w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-xs transition-colors hover:border-gray-400 focus:border-gray-500 focus:ring-2 focus:ring-gray-500/20 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400 sm:h-10 sm:text-sm"
								optionClassName="ps-6"
								enableVirtualization={true}
								itemHeight={36}
								maxVisibleItems={8}
							/>
						</Field>

						{/* ---- Preview Section ---- */}
						{canSubmit && (
							<div className="relative overflow-hidden rounded-lg border border-emerald-200 bg-linear-to-br from-emerald-50 to-emerald-50/80 p-4 shadow-sm">
								<div className="relative">
									<span className="mb-2 block font-medium text-emerald-700 text-xs uppercase tracking-widest">
										Your Exercise
									</span>
									<div className="flex items-center gap-3">
										<span className="truncate font-semibold text-emerald-900 text-sm">
											{selectedMovement.name}
										</span>
										<Badge
											variant="outline"
											className="border-emerald-300 bg-white px-2.5 py-1 font-medium text-emerald-700 text-xs shadow-sm"
										>
											{selectedEquipment.name}
										</Badge>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* ---- Footer ---- */}
					<div className="border-gray-200 border-t bg-white/90 px-4 py-4 backdrop-blur-sm sm:px-5">
						<div className="flex items-center justify-between gap-3">
							<button
								type="button"
								onClick={handleCancel}
								className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 text-xs transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:px-4 sm:py-2 sm:text-sm"
							>
								Cancel
							</button>
							<button
								type="submit"
								disabled={!canSubmit}
								className="rounded-lg bg-gray-900 px-3 py-1.5 font-medium text-white text-xs transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm"
							>
								Create Exercise
							</button>
						</div>
					</div>
				</form>
			</div>
		</Drawer>
	);
}

function ExerciseInfoPopover() {
	return (
		<Popover>
			<PopoverButton className="flex size-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 shadow-sm transition-colors duration-200 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500/20">
				<InformationCircleIcon className="size-5" />
			</PopoverButton>

			<PopoverPanel
				anchor="top"
				className="z-50 w-80 rounded-lg border border-gray-200 bg-white shadow-xl [--anchor-gap:8px] [--anchor-padding:8px]"
			>
				<div className="max-h-[400px] overflow-y-auto bg-linear-to-b from-white to-gray-50/50 p-4 sm:p-5">
					<div className="space-y-4">
						{/* Header */}
						<div className="border-gray-200 border-b pb-3">
							<h4 className="font-semibold text-base text-gray-900 sm:text-lg">
								What is an Exercise?
							</h4>
						</div>

						{/* Description */}
						<div className="text-gray-700 text-sm leading-relaxed">
							An exercise is a specific combination of a movement pattern and equipment. For
							example, "Barbell Squat" combines the "squat" movement with "barbell" equipment.
						</div>

						{/* Movement Creation Info */}
						<div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-blue-900 text-xs sm:text-sm">
							<strong className="font-semibold">💡 Tip:</strong> You can search for existing
							movements or create a new one by typing a movement name that doesn't exist yet. A
							"Create Movement" button will appear for new movements.
						</div>

						{/* How to create */}
						<div className="space-y-3">
							<h5 className="font-semibold text-gray-900 text-sm sm:text-base">
								How to create an exercise:
							</h5>
							<div className="space-y-2">
								<div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-gray-700 text-xs shadow-sm sm:text-sm">
									<strong className="font-semibold text-gray-900">1. Choose Movement:</strong>{' '}
									Select an existing movement pattern like "squat", "press", "pull", or "row", or
									create a new one by typing a name
								</div>
								<div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-gray-700 text-xs shadow-sm sm:text-sm">
									<strong className="font-semibold text-gray-900">2. Select Equipment:</strong> Pick
									the primary equipment used for loading (barbell, dumbbell, bodyweight, etc.)
								</div>
								<div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-gray-700 text-xs shadow-sm sm:text-sm">
									<strong className="font-semibold text-gray-900">3. Exercise Created:</strong> The
									system combines them into a unique exercise (e.g., "Dumbbell Press")
								</div>
							</div>
						</div>

						{/* Examples */}
						<div className="space-y-3">
							<h5 className="font-semibold text-gray-900 text-sm sm:text-base">Examples:</h5>
							<div className="space-y-2">
								<div className="rounded-lg border border-gray-200 border-l-4 border-l-emerald-300 bg-white px-3 py-2.5 text-gray-700 text-xs shadow-sm sm:text-sm">
									<div>Movement: "Squat"</div>
									<div>Equipment: "Barbell"</div>
									<div className="font-semibold text-gray-900">→ Exercise: "Barbell Squat"</div>
								</div>
								<div className="rounded-lg border border-gray-200 border-l-4 border-l-emerald-300 bg-white px-3 py-2.5 text-gray-700 text-xs shadow-sm sm:text-sm">
									<div>Movement: "Press"</div>
									<div>Equipment: "Dumbbell"</div>
									<div className="font-semibold text-gray-900">→ Exercise: "Dumbbell Press"</div>
								</div>
								<div className="rounded-lg border border-gray-200 border-l-4 border-l-emerald-300 bg-white px-3 py-2.5 text-gray-700 text-xs shadow-sm sm:text-sm">
									<div>Movement: "Pull-up"</div>
									<div>Equipment: "Bodyweight"</div>
									<div className="font-semibold text-gray-900">
										→ Exercise: "Bodyweight Pull-up"
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</PopoverPanel>
		</Popover>
	);
}

export { CreateUserExerciseDrawerV2 };
